import { useEffect, useState, useMemo } from "react";
import { Navigate } from "react-router-dom";
import {
  Download, RefreshCw, FileText, Loader2,
  ChevronDown, Trash2, Inbox, Briefcase, Mail, Paperclip,
} from "lucide-react";
import { toast } from "sonner";
import { adminApi, getAdminToken } from "@/lib/adminApi";
import AdminShell from "@/components/admin/AdminShell";

const TABS = [
  { key: "quote-requests",   label: "Quote Requests",   icon: Inbox,     short: "Quotes" },
  { key: "job-applications", label: "Job Applications", icon: Briefcase, short: "Jobs" },
  { key: "contact-messages", label: "Contact Messages", icon: Mail,      short: "Contacts" },
];

const STATUSES = [
  { value: "new",       label: "New",       cls: "bg-[#2BB6E5] text-[#050B14]" },
  { value: "contacted", label: "Contacted", cls: "bg-[#BEE7F5] text-[#0E2A57]" },
  { value: "closed",    label: "Closed",    cls: "bg-[#E5EBF2] text-[#475569]" },
];

function StatusBadge({ value, onChange, testId }) {
  const cfg = STATUSES.find((s) => s.value === value) || STATUSES[0];
  return (
    <div className="relative inline-block group">
      <button
        type="button"
        data-testid={testId}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] font-medium ${cfg.cls}`}
      >
        {cfg.label} <ChevronDown size={12} />
      </button>
      <div className="absolute right-0 top-full mt-1 hidden group-hover:block group-focus-within:block bg-white border border-[#0E2A57]/20 shadow-xl z-20 min-w-[140px]">
        {STATUSES.map((s) => (
          <button
            key={s.value}
            type="button"
            onClick={() => onChange(s.value)}
            className="w-full text-left px-3 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-[#0E2A57] hover:bg-[#F4F8FC]"
          >
            {s.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IE", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch (e) {
    return String(iso);
  }
}

export default function AdminDashboard() {
  const [activeTab, setActiveTab] = useState("quote-requests");
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const hasToken = !!getAdminToken();

  const loadAll = async () => {
    setLoading(true);
    try {
      const [list, st] = await Promise.all([
        adminApi.get(`/admin/${activeTab}`),
        adminApi.get(`/admin/stats`),
      ]);
      setRows(list.data || []);
      setStats(st.data || null);
    } catch (err) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (hasToken) loadAll(); /* eslint-disable-line */ }, [activeTab, hasToken]);

  const onStatusChange = async (id, status) => {
    try {
      await adminApi.patch(`/admin/${activeTab}/${id}/status`, { status });
      setRows((rs) => rs.map((r) => (r.id === id ? { ...r, status } : r)));
      toast.success(`Status → ${status}`);
      // refresh stats counter
      const st = await adminApi.get("/admin/stats");
      setStats(st.data);
    } catch (err) {
      toast.error("Failed to update status");
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Permanently delete this record? This cannot be undone.")) return;
    try {
      await adminApi.delete(`/admin/${activeTab}/${id}`);
      setRows((rs) => rs.filter((r) => r.id !== id));
      toast.success("Deleted");
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const onExportCsv = async () => {
    try {
      const res = await adminApi.get(`/admin/${activeTab}/export.csv`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ags-${activeTab}-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Failed to export CSV");
    }
  };

  const onDownloadFile = async (id, originalName) => {
    try {
      const res = await adminApi.get(`/admin/${activeTab}/${id}/file`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = originalName || "attachment";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("File unavailable");
    }
  };

  const filtered = useMemo(() => {
    return (rows || []).filter((r) => {
      if (statusFilter && (r.status || "new") !== statusFilter) return false;
      if (!query.trim()) return true;
      const q = query.toLowerCase();
      const blob = [
        r.company_name, r.contact_person, r.full_name, r.email, r.phone,
        r.subject, r.role, r.site_type, r.message,
      ].filter(Boolean).join(" ").toLowerCase();
      return blob.includes(q);
    });
  }, [rows, query, statusFilter]);

  const statKey = activeTab.replace("-", "_");
  const headerStat = stats?.[statKey];

  if (!hasToken) {
    return <Navigate to="/admin/login" replace />;
  }

  return (
    <AdminShell title="Leads Dashboard">
      <div data-testid="admin-dashboard">
        {/* Stat cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#0E2A57]/15 border border-[#0E2A57]/15 mb-10">
          {TABS.map((t) => {
            const Icon = t.icon;
            const ts = stats?.[t.key.replace("-", "_")];
            return (
              <button
                key={t.key}
                onClick={() => setActiveTab(t.key)}
                data-testid={`stat-card-${t.key}`}
                className={`bg-white px-6 py-7 text-left transition-colors hover:bg-[#F4F8FC] ${activeTab === t.key ? "ring-2 ring-inset ring-[#0E2A57]" : ""}`}
              >
                <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-[#0E2A57]/55 font-medium">
                  <Icon size={12} /> {t.label}
                </div>
                <div className="mt-4 flex items-baseline gap-3">
                  <div className="font-heading font-extrabold text-4xl md:text-5xl tracking-[-0.04em] text-[#050B14]">
                    {ts?.total ?? "—"}
                  </div>
                  {ts?.new > 0 && (
                    <span className="bg-[#2BB6E5] text-[#050B14] px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.18em] font-medium">
                      {ts.new} new
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {/* Tab header + controls */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
          <div>
            <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#0E2A57]/60 font-medium">
              {headerStat ? `${headerStat.total} records · ${headerStat.new} new` : "Loading"}
            </div>
            <h1 className="mt-2 font-heading font-extrabold text-3xl md:text-4xl tracking-[-0.035em] text-[#050B14]">
              {TABS.find((t) => t.key === activeTab)?.label}
            </h1>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search…"
              data-testid="admin-search"
              className="bg-white border border-[#0E2A57]/15 px-4 py-2.5 text-sm focus:border-[#0E2A57] focus:outline-none w-44 md:w-60"
            />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              data-testid="admin-status-filter"
              className="bg-white border border-[#0E2A57]/15 px-4 py-2.5 text-sm focus:border-[#0E2A57] focus:outline-none"
            >
              <option value="">All statuses</option>
              {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
            <button
              onClick={loadAll}
              data-testid="admin-refresh"
              className="inline-flex items-center gap-2 border border-[#0E2A57]/30 text-[#0E2A57] hover:bg-[#0E2A57] hover:text-white px-3 py-2.5 font-mono text-[10px] uppercase tracking-[0.22em] font-medium transition-colors"
            >
              <RefreshCw size={12} /> Refresh
            </button>
            <button
              onClick={onExportCsv}
              data-testid="admin-export-csv"
              className="inline-flex items-center gap-2 bg-[#0E2A57] text-white hover:bg-[#050B14] px-3 py-2.5 font-mono text-[10px] uppercase tracking-[0.22em] font-medium transition-colors"
            >
              <Download size={12} /> Export CSV
            </button>
          </div>
        </div>

        {/* Records table */}
        <div className="bg-white border border-[#0E2A57]/15">
          {loading ? (
            <div className="p-12 flex items-center justify-center text-[#475569]">
              <Loader2 size={20} className="animate-spin mr-3" /> Loading…
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center text-[#475569] font-mono text-[11px] uppercase tracking-[0.22em]">
              No records {query || statusFilter ? "match your filter" : "yet"}
            </div>
          ) : (
            <ul className="divide-y divide-[#E5EBF2]">
              {filtered.map((r) => {
                const open = expanded === r.id;
                const fileName = r.attachment_filename || r.cv_filename;
                const primary = r.company_name || r.full_name || r.subject || "—";
                const subtitle = r.contact_person || r.role || r.email;
                return (
                  <li key={r.id} data-testid={`admin-row-${r.id}`} className="group">
                    <div className="px-5 md:px-6 py-5 flex flex-wrap items-center gap-4 hover:bg-[#F4F8FC] transition-colors">
                      <button
                        type="button"
                        onClick={() => setExpanded(open ? null : r.id)}
                        className="flex-1 min-w-0 text-left"
                      >
                        <div className="flex items-center gap-3">
                          <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#0E2A57]/45 w-24 flex-shrink-0">
                            {formatDate(r.created_at).split(",")[0]}
                          </span>
                          <span className="font-heading font-bold text-[15px] text-[#050B14] tracking-[-0.02em] truncate">
                            {primary}
                          </span>
                        </div>
                        <div className="mt-1 ml-[6.5rem] flex flex-wrap items-center gap-3 text-xs text-[#475569]">
                          <span>{subtitle}</span>
                          {r.email && <a href={`mailto:${r.email}`} onClick={(e)=>e.stopPropagation()} className="hover:text-[#0E2A57]">{r.email}</a>}
                          {fileName && (
                            <span className="inline-flex items-center gap-1 text-[#0E2A57]">
                              <Paperclip size={10} /> {fileName}
                            </span>
                          )}
                        </div>
                      </button>
                      <StatusBadge
                        value={r.status || "new"}
                        onChange={(s) => onStatusChange(r.id, s)}
                        testId={`status-${r.id}`}
                      />
                    </div>
                    {open && (
                      <div className="px-5 md:px-6 py-6 bg-[#F4F8FC] border-t border-[#E5EBF2]">
                        <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-3 text-sm">
                          {Object.entries(r).filter(([k]) => !["id", "created_at_iso", "attachment_stored_name", "cv_stored_name"].includes(k)).map(([k, v]) => (
                            <div key={k} className="flex gap-4">
                              <dt className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#0E2A57]/55 w-32 flex-shrink-0 pt-1">{k}</dt>
                              <dd className="text-[#050B14] break-words whitespace-pre-wrap">{v === null || v === "" || v === undefined ? "—" : String(v)}</dd>
                            </div>
                          ))}
                        </dl>
                        <div className="mt-6 pt-5 border-t border-[#E5EBF2] flex flex-wrap items-center gap-3">
                          {fileName && (
                            <button
                              onClick={() => onDownloadFile(r.id, fileName)}
                              data-testid={`download-file-${r.id}`}
                              className="inline-flex items-center gap-2 bg-[#0E2A57] text-white hover:bg-[#050B14] px-4 py-2 font-mono text-[10px] uppercase tracking-[0.22em] font-medium transition-colors"
                            >
                              <FileText size={12} /> Download {fileName}
                            </button>
                          )}
                          {r.email && (
                            <a
                              href={`mailto:${r.email}?subject=${encodeURIComponent('AGS Cleaning Services — re: your enquiry')}`}
                              className="inline-flex items-center gap-2 border border-[#0E2A57]/30 text-[#0E2A57] hover:bg-[#0E2A57] hover:text-white px-4 py-2 font-mono text-[10px] uppercase tracking-[0.22em] font-medium transition-colors"
                            >
                              <Mail size={12} /> Reply
                            </a>
                          )}
                          <button
                            onClick={() => onDelete(r.id)}
                            data-testid={`delete-${r.id}`}
                            className="inline-flex items-center gap-2 border border-red-300 text-red-700 hover:bg-red-700 hover:text-white px-4 py-2 font-mono text-[10px] uppercase tracking-[0.22em] font-medium transition-colors ml-auto"
                          >
                            <Trash2 size={12} /> Delete
                          </button>
                        </div>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </AdminShell>
  );
}
